import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { isCalculatedValue } from 'app/utils/is-calculated-value';
import { noPrintout } from 'app/utils/no-printout'
import { getProductName } from 'app/utils/product-utils';
import {
  AppArticle as Article,
  AppConfigurationSection as ConfigurationSection,
  AppConfigurationVariable as ConfigurationVariable,
  AppCustomLine as CustomLine,
  AppProductLine as ProductLine,
  ProductAssignment, SnkProduct
} from 'graphql/models/extended';
import {
  ConfigurationVariableValue,
  DocumentVersionInput
} from 'graphql/models/generated';
import { ProductPositionVariableQuery } from 'graphql/queries/product-position-variable';
import { LineSubtypePropVal } from 'models/position.model';

import { PositionCustomPropertiesService } from './position-custom-properties.service';

@Injectable({
  providedIn: 'root'
})
export class OfferPositionProductService {
  constructor(
    private router: Router,
    private posCustomPropsService: PositionCustomPropertiesService,
    private productPositionVariableQuery: ProductPositionVariableQuery
  ) { }

  getPositionVariable(
    documentVersion: DocumentVersionInput,
    lineId: string,
    variableId: string
  ): Observable<ConfigurationVariable> {
    const variables = { documentVersion, lineId, variableId };

    return this.productPositionVariableQuery.fetch(variables, { fetchPolicy: 'no-cache' })
      .pipe<ConfigurationVariable, ConfigurationVariable>(
        map((res) => (
          res.data?.quotes?.quote?.line as ProductLine
        )?.configuration?.variable as ConfigurationVariable),
        take(1)
      );
  }

  mapFilteredVariables(pos: ProductLine, parentPosition?: ProductLine): SnkProduct {
    let position = { ...pos };
    let product = this.getProductBaseFromLine(position) as SnkProduct;

    const mapSectionsVariables = Boolean(position?.configuration?.sections);

    if (mapSectionsVariables && position) {
      const currentAssignments = position.product?.assignments
        || this.extractCurrentAssignments(position);

      const currentProductAssignments = position.product?.productAssignments || this.extractCurrentAssignments(position);

      product.productName = getProductName(position)

      product.productAssignments = [];
      product.positionAssignments = [];
      if (!currentAssignments.length) { return product; }

      if (parentPosition) {
        // In calculation sheet, we have access to the parent position,
        // so we can compare child assignments with parent
        if (position.id === parentPosition.id) {
          // extract parent/group assignments
          product.productAssignments = currentProductAssignments
            .filter((i) => this.isPrintoutAssignment(i));
        } else {
          const parentAssignments = parentPosition.product.assignments
            || this.extractCurrentAssignments(parentPosition);
          // extract position assignments, by comparing them to the parent
          product.positionAssignments = this.filterAssignments(currentAssignments, parentAssignments)
            .filter((item) => this.removeDuplicateAssignments(item));
        }
      } else {
        // In configuration page we don't need to show the differences between child and parent,
        // so we don't need to compare child assignments with parent
        // exception is duplicate group in calculation sheet

        const filterOutEmptyAssignments = ['/offer'].some((r) => this.router.url.startsWith(r));
        if (filterOutEmptyAssignments) {
          product.productAssignments = currentAssignments
            .filter((i) => this.isPrintoutAssignment(i));
        } else {
          product.productAssignments = currentAssignments;
        }
      }
    }

    return product;
  }

  removeDuplicateAssignments(item: ProductAssignment): boolean {
    // this regex filtering dimensions and measurements fields for all manufactures
    const regexData = [
      '(.(?:Z_|STZ_|FU_|BB_|TB_|):?(Breite|Hoehe|DIN|Dicke|Wandstaerke|ATTRIB|Masse?))'
    ];
    // regex demo: https://regex101.com/r/nvNabG/1

    const regex = new RegExp(`${regexData}`, 'ig');
    const exceptions = ['Leibungsverkleidung', 'Glas.'];

    return !regex.test(item.id) || exceptions.some((e) => item.id.includes(e));
  }

  isPrintoutAssignment(item: ProductAssignment) {
    return !item.noPrintout;
  }

  filterAssignments(
    posList: ProductAssignment[],
    baseList: ProductAssignment[],
    compareValue = true
  ): ProductAssignment[] {
    return posList.filter((a) => !baseList.some((b) => b.id === a.id && (compareValue ? b.value === a.value : true)));
  }

  extractCurrentAssignments(pos: ProductLine): ProductAssignment[] {
    const assignments: ProductAssignment[] = [];
    ((pos.configuration && pos.configuration.sections) || [])
      .forEach((s) => {
        // flatten sections
        let items: ConfigurationVariable[] = [];

        const processItems = !s.id.startsWith('Filter');
        if (processItems) {
          if (s.variables && s.variables.items) {
            items = s.variables.items as ConfigurationVariable[];
          } else if (s.sections) {
            items = s.sections.map(
              (ss) => (ss.variables && ss.variables.items) || []
            ).flat() as ConfigurationVariable[];
          }
        }

        items.forEach((item) => {
          let val;
          if (isCalculatedValue(item)) {
            const calcValue = (item.calculatedValue && item.calculatedValue.valueText) || '';
            val = { name: calcValue, value: calcValue };
          } else if (item.assignedValues && item.assignedValues.length) {
            val = item.assignedValues[0];
          } else if (item.values && item.values.length) {
            val = item.values.find((v) => (
              v.assigned != null
            ));
          }

          if (val) {
            assignments.push({
              id: item.id,
              varName: item.name,
              name: val.name,
              value: val.value,
              noPrintout: noPrintout(item) || noPrintout(val as ConfigurationVariableValue)
            });
          }
        });
      });

    return assignments;
  }

  isOtherConfigurableProduct(productName: string): boolean {
    return productName.toLowerCase().includes('sonstige');
  }

  /**
   * Adds `validationRequired = true` on invalid positions inside the `positions`
   * array.
   */
  validatePositions(
    positions: ProductLine[]
  ): {
    id: ConfigurationVariable['id'];
    lineId: ProductLine['id'];
    sectionId: ConfigurationSection['id'];
  }[] {
    const invalid: {
      id: ConfigurationVariable['id'];
      lineId: ProductLine['id'];
      sectionId: ConfigurationSection['id'];
    }[] = [];

    for (const pos of positions) {
      if (!pos.configuration) {
        // eslint-disable-next-line no-continue
        continue;
      }

      for (const section of pos.configuration.sections) {
        if (!section.variables) {
          // eslint-disable-next-line no-continue
          continue;
        }

        for (const item of section.variables.items as ConfigurationVariable[]) {
          if (item && item.required) {
            // required if: false, null, undefined, NaN, empty string
            // but not required if 0
            if (!item.value && item.value !== 0) {
              item.validationRequired = true;
              invalid.push({
                id: item.id,
                lineId: pos.id,
                sectionId: section.id
              });
            } else {
              // if this was bad before, it is now fixed and we can continue
              item.validationRequired = false;
            }
          }
        }
      }
    }

    return invalid;
  }

  getProductBaseFromLine(position: ProductLine | CustomLine): SnkProduct | Article {
    const posType: LineSubtypePropVal = this.posCustomPropsService.getLineSubtype(position);
    // Free Articles
    if (
      posType === LineSubtypePropVal.FREE_ARTICLE
      || ('article' in position && position.article === null && 'articleCustom' in position && position.articleCustom === null)
    ) {
      let product = ( ('article' in position && position.article) || ('articleCustom' in position && position.articleCustom)
        ? (position.articleCustom || position.article) as Article
        : null
      );

      return {
        ...product,
        customName: (position as CustomLine).customProductName || this.posCustomPropsService.getLineKurztext(position),
        customDescription: (position as CustomLine).customProductText || this.posCustomPropsService.getFreeArticleDescription(position),
        customType: LineSubtypePropVal.FREE_ARTICLE
      } as Article;
    }

    // My Articles
    if (
      posType === LineSubtypePropVal.MY_ARTICLE &&
      (('article' in position && position.article) || ('articleCustom' in position && position.articleCustom))
    ) {
      let customDescription = this.replaceTextLineBreaks((position as CustomLine).customProductText || this.posCustomPropsService.getLineArticleDescription(position));

      return {
        ...(position.articleCustom || position.article) as Article,
        customName: (position as CustomLine).customProductName || this.posCustomPropsService.getLineKurztext(position),
        customDescription,
        customType: LineSubtypePropVal.MY_ARTICLE
      } as Article;
    }

    // Loose Articles
    if ((position as ProductLine).product && !(position as ProductLine).product.isConfigurable) {
      let customDescription = this.replaceTextLineBreaks((position as ProductLine)?.snkProductText || '');

      return {
        ...(position as ProductLine).product as SnkProduct,
        productName: (position as ProductLine)?.snkProductName || (position as ProductLine).product?.description,
        customDescription,
        customType: LineSubtypePropVal.LOOSE_ARTICLE
      };
    }

    // Product
    return {
      ...(position as ProductLine).product as SnkProduct,
      customType: LineSubtypePropVal.PRODUCT
    };
  }

  private replaceTextLineBreaks(text: string | undefined): string | undefined {
    if (!text) {
      return text;
    }

    return text.replace(/\r\n|\r|\n/g, '<br>');
  }
}
